import React, { useEffect, useRef, useState } from 'react';
import { IStepModuleProps, ModuleSearchParam } from '../../../../models/modules';
import { IReservationModel } from '../../../../entities/reservation';
import { IService, IServiceDetails } from '../../../../entities/service';
import { getServices, getServicesByCalendar } from '../../../../services/api/service';
import makeCancelable, { ICancelablePromise } from '../../../../services/cancelablePromise';
import { StepType } from '../../../../services/getStepOrder';
import withSearchParams, { ISearchParamsOptions } from '../withSearchParams';
import ServiceItem, { ServiceItemSkeleton } from './ServiceItem/ServiceItem';
import AvailabilityWarning from 'src/components/AvailabilityWarning';
import { isSame } from 'src/entities/common';
import SelectButton from '../SelectButton';
import { getPaymentNeed } from '../../ReservationEnd/functions/helperFunctions';

interface IServiceSelectProps extends IStepModuleProps {
    shouldGetByCalendar?: boolean;
    isPaymentEnabled?: boolean;
}

const searchParamsOptions: ISearchParamsOptions<IService> = {
    paramName: ModuleSearchParam.ServiceId,
};

export const ServiceSelect: React.FC<IServiceSelectProps> = ({
    shouldGetByCalendar,
    isPaymentEnabled,
    data,
    isActive,
    isFirst,
    onComplete = () =>
        // eslint-disable-next-line no-console
        console.warn(
            'ServiceSelect module onComplete event is not set, please attach an event handler'
        ),
    onIsModifiable,
    onLoadingChanged,
}) => {
    const servicesSubscription = useRef<ICancelablePromise<IServiceDetails[]>>();
    const selectedService = data[StepType.Service] as IServiceDetails;
    const [services, setServices] = useState<IServiceDetails[]>([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [prevHasLoaded, setPrevHasLoaded] = useState(false);
    const [shouldPreSelect, setShouldPreSelect] = useState(false);
    const hasAnyLogo = services.some((service) => service.PictureLink);
    const prevProps = useRef({ isActive, data });

    const fetchServices = async () => {
        setHasLoaded(false);

        const calendar = data[StepType.Calendar];

        if (shouldGetByCalendar && calendar) {
            servicesSubscription.current = makeCancelable<IServiceDetails[]>(
                getServicesByCalendar(calendar.Id)
            );
        } else {
            servicesSubscription.current = makeCancelable<IServiceDetails[]>(getServices(true));
        }
        try {
            const servicesFormSubscription = await servicesSubscription.current.promise;
            setServices(servicesFormSubscription);
            setHasLoaded(true);
        } catch (err) {
            return;
        }
    };

    const selectService = (service: IServiceDetails, hasSkipped?: boolean) => {
        if (isActive) {
            onComplete(createSelectedService(service), hasSkipped);
        }
    };

    const createSelectedService = (service: IServiceDetails): IReservationModel => ({
        ...data,
        [StepType.Service]: {
            ...service,
            displayValue: service.Name,
        },
    });

    const checkSelectedService = () => {
        if (!data[StepType.Service]) {
            const { searchParams } = data;
            if (searchParams) {
                const searchService = searchParams.ServiceId;
                if (searchService) {
                    if (services) {
                        const service = services.find((s) => s.Id === +searchService);
                        if (service) {
                            selectService(service);
                        }
                    }
                }
            }
        }
    };

    const isServiceEmpty = (service: IServiceDetails) => {
        return !service.PictureLink && !service.ShortDescription && !service.Description;
    };

    const hasSingleEmptyService = () => {
        return (
            services.length === 1 &&
            isServiceEmpty(services[0]) &&
            !getPaymentNeed(isPaymentEnabled, services[0].Payable)
        );
    };

    const preSelect = () => {
        if (hasLoaded) {
            const isSingleEmptyService = hasSingleEmptyService();

            if (isSingleEmptyService) {
                selectService(services[0], true);
            }

            if (onIsModifiable) {
                onIsModifiable(!isSingleEmptyService);
            }
        }
    };

    useEffect(() => {
        if (isActive || (!hasLoaded && data[StepType.Service])) {
            fetchServices().then(() => setShouldPreSelect(true));
        }

        return () => {
            servicesSubscription?.current?.cancel();
        };
    }, []);

    useEffect(() => {
        if (hasLoaded) {
            checkSelectedService();
        }
    }, [hasLoaded]);

    useEffect(() => {
        if (!isActive) {
            prevProps.current = { isActive, data };
            return;
        }

        const isActiveChanged = prevProps.current.isActive !== isActive;
        const calendarChanged = !isSame(
            prevProps.current.data[StepType.Calendar],
            data[StepType.Calendar]
        );
        const shouldFetch =
            isActiveChanged && (!hasLoaded || (shouldGetByCalendar && calendarChanged));

        if (shouldFetch) {
            fetchServices().then(() => setShouldPreSelect(true));
        } else {
            setShouldPreSelect(true);
        }

        if (prevHasLoaded !== hasLoaded) {
            onLoadingChanged?.(!hasLoaded);
        }

        prevProps.current = { isActive, data };
        setPrevHasLoaded(hasLoaded);
    }, [isActive, data, shouldGetByCalendar, hasLoaded]);

    useEffect(() => {
        if (shouldPreSelect) {
            preSelect()
            setShouldPreSelect(false);
        }        
    }, [shouldPreSelect])
    

    if (!hasLoaded) {
        return (
            <>
                <ServiceItemSkeleton />
                <ServiceItemSkeleton />
            </>
        );
    }

    if (!services.length) {
        return <AvailabilityWarning type="bookingNotAvailable" />;
    }

    return (
        <section className="service-select">
            {services.map((service, i) => (
                <ServiceItem
                    key={service.Id}
                    service={service}
                    selectService={selectService}
                    needsPayment={getPaymentNeed(isPaymentEnabled, service.Payable)}
                    button={
                        <SelectButton
                            autoFocus={i === 0}
                            isFirstStep={!!isFirst}
                            onClick={() => selectService(service)}
                            selected={selectedService?.Id === service.Id}
                        />
                    }
                    withLogoSpace={hasAnyLogo}
                />
            ))}
        </section>
    );
};

export default withSearchParams<IServiceSelectProps, IService>(ServiceSelect, searchParamsOptions);
